import { CheckCircle2, XCircle, Loader2, Zap, SquareArrowOutUpRight, ListFilter } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface EmailActivityCardProps {
  email: {
    id: number
    fromName: string | null
    fromEmail: string | null
    subject: string | null
    receivedAt: string
    gmailMessageId: string
    processingStatus: string
    stats: {
      workflowsPassed: number
      workflowsFailed: number
      totalActions: number
      totalWorkflows: number
    }
  }
  onClick?: () => void
  gmailSessionId?: number
  isFirst?: boolean
  isLast?: boolean
  timezone?: string
}

export default function EmailActivityCard({ email, onClick, gmailSessionId = 0, isFirst = false, isLast = false, timezone = 'America/New_York' }: EmailActivityCardProps) {
  const {
    fromName,
    fromEmail,
    subject,
    receivedAt,
    gmailMessageId,
    processingStatus,
    stats,
  } = email

  const gmailUrl = `https://mail.google.com/mail/u/${gmailSessionId}/#all/${gmailMessageId}`

  // Format timestamp with timezone
  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const currentYear = now.getFullYear()
    const dateYear = date.getFullYear()

    // Format date in the specified timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      month: 'short',
      day: 'numeric',
      year: dateYear < currentYear ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })

    const parts = formatter.formatToParts(date)
    const month = parts.find(p => p.type === 'month')?.value || ''
    const day = parts.find(p => p.type === 'day')?.value || ''
    const year = parts.find(p => p.type === 'year')?.value
    const hour = parts.find(p => p.type === 'hour')?.value || ''
    const minute = parts.find(p => p.type === 'minute')?.value || ''
    const dayPeriod = parts.find(p => p.type === 'dayPeriod')?.value || ''

    // Get timezone abbreviation
    const tzFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
    const tzParts = tzFormatter.formatToParts(date)
    const tzAbbr = tzParts.find(p => p.type === 'timeZoneName')?.value || 'EST'

    // Format hour:minute with AM/PM (12-hour format)
    const formattedTime = `${hour}:${minute.padStart(2, '0')} ${dayPeriod.toUpperCase()}`

    if (year && parseInt(year) < currentYear) {
      return `${month} ${day}, ${year} ⋅ ${formattedTime} ${tzAbbr}`
    } else {
      return `${month} ${day} ⋅ ${formattedTime} ${tzAbbr}`
    }
  }

  const formattedTimestamp = formatTimestamp(receivedAt)

  // Determine status display for icon based on workflow stats
  const getStatusDisplay = () => {
    // If actively processing, always show processing status
    if (processingStatus === 'processing') {
      return {
        icon: Loader2,
        iconColor: 'text-blue-600 dark:text-blue-400',
        iconAnimation: 'animate-spin',
      }
    }

    // Determine icon based on workflow results
    if (stats.workflowsPassed === 0) {
      // Zero workflows passed - red X
      return {
        icon: XCircle,
        iconColor: 'text-red-600 dark:text-red-400',
        iconAnimation: '',
      }
    } else if (stats.workflowsPassed > 0) {
      // At least one workflow passed - green check
      return {
        icon: CheckCircle2,
        iconColor: 'text-green-600 dark:text-green-400',
        iconAnimation: '',
      }
    } else {
      // Default/unknown - gray loader
      return {
        icon: Loader2,
        iconColor: 'text-gray-600 dark:text-gray-400',
        iconAnimation: 'animate-spin',
      }
    }
  }

  const statusDisplay = getStatusDisplay()
  const StatusIcon = statusDisplay.icon

  const handleGmailLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.open(gmailUrl, '_blank')
  }

  const roundedCorners = isFirst && isLast
    ? 'rounded-lg'
    : isFirst
      ? 'rounded-t-lg'
      : isLast
        ? 'rounded-b-lg'
        : ''

  return (
    <div
      className={`cursor-pointer transition-colors bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${roundedCorners} ${!isLast ? 'border-b border-gray-300 dark:border-gray-700/50' : ''}`}
      onClick={onClick}
    >
      <div className="px-6 py-3 sm:py-4">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Header: From Name/Email and Status Icon */}
            <div className="flex items-start gap-2 sm:gap-3 mb-2">
              <StatusIcon
                className={`w-3 h-3 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0 ${statusDisplay.iconColor} ${statusDisplay.iconAnimation}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                  <p className="font-semibold text-sm sm:text-base text-blue-950 dark:text-white truncate">
                    {fromName || fromEmail || 'Unknown Sender'}
                  </p>
                  {fromName && fromEmail && (
                    <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                      {fromEmail}
                    </span>
                  )}
                </div>
                {subject && (
                  <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mb-2 line-clamp-2 sm:truncate">
                    {subject}
                  </p>
                )}
                {/* Badges with timestamp on same line */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1 px-1.5 py-0.5 border border-gray-300 dark:border-gray-600/50 rounded bg-transparent text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
                    <ListFilter className="w-2 h-2 sm:w-2.5 sm:h-2.5 flex-shrink-0" />
                    <span className="whitespace-nowrap">
                      {stats.workflowsPassed} of {stats.totalWorkflows} {stats.totalWorkflows === 1 ? 'workflow' : 'workflows'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 px-1.5 py-0.5 border border-gray-300 dark:border-gray-600/50 rounded bg-transparent text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
                    <Zap className="w-2 h-2 sm:w-2.5 sm:h-2.5 flex-shrink-0" />
                    <span className="whitespace-nowrap">
                      {stats.totalActions} {stats.totalActions === 1 ? 'action' : 'actions'}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formattedTimestamp}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* View in Gmail Button - Right Side */}
          <div className="flex items-center flex-shrink-0">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleGmailLinkClick}
              className="h-auto px-3 py-1.5 text-xs dark:bg-gray-700 dark:hover:bg-gray-600 [&_svg]:!w-3 [&_svg]:!h-3"
              type="button"
              iconRight={<SquareArrowOutUpRight />}
            >
              View in Gmail
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
